import { useCallback, useEffect, useState } from "react";
import { ApiError, demoApi, problemMessage } from "@/api/client";
import { readMyTimeSnapshot, writeMyTimeSnapshot } from "@/offline/myTimeCache";
import type { MyTime } from "@/api/types";

export type MyTimeStatus = "loading" | "ready" | "error";

export interface UseMyTimeResult {
  /**
   * `null` until the first fetch resolves, and on failure. Deliberately not a
   * default-shaped object: "we do not know whether a shift is running" and "no shift is
   * running" are different answers, and collapsing them would render a confident
   * "Clock in" to somebody who is already clocked in — the one mistake in this screen
   * that costs a person their hours.
   */
  data: MyTime | null;
  status: MyTimeStatus;
  error: string | null;
  /** Re-reads after a punch or a manual entry. Never optimistic — see the note below. */
  reload: () => Promise<void>;
}

/**
 * The employee's own time — GET /api/demo/time/mine.
 *
 * ONE call backs the whole screen: the running shift, the recent days, and the
 * business's recording mode. The alternative (a mode lookup plus a list) would be two
 * chances for the screen to contradict itself about what it is allowed to offer.
 *
 * <b>Every write re-reads rather than patching state locally.</b> The figures that matter
 * here are the server's — hours are derived from ITS clock, and status is decided by the
 * farmer — so an optimistic update would be the phone inventing a number it is
 * specifically not allowed to invent. It is one small request on a screen that is used a
 * handful of times a day.
 */
export function useMyTime(): UseMyTimeResult {
  const [data, setData] = useState<MyTime | null>(null);
  const [status, setStatus] = useState<MyTimeStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      const resolved = await demoApi.getMyTime(signal);
      if (signal?.aborted) return;
      setData(resolved);
      setStatus("ready");
      setError(null);
      // Write-through, so a later reload with no signal survives (ef-017 AC8) — the
      // same "last known good" pattern useReferenceData.ts already uses for vendors
      // and categories.
      writeMyTimeSnapshot(resolved);
    } catch (err) {
      if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) return;

      // Before falling back to an error screen a worker with no signal cannot get
      // past: use the last shift/recent-days list the API gave THIS device. Never
      // pretend it is fresher than it is — the reload itself is the only proof the
      // worker has that something recovered; this is what lets the screen render at
      // all in the meantime.
      if (err instanceof ApiError && err.neverReachedTheServer) {
        const cached = readMyTimeSnapshot();
        if (cached) {
          setData(cached);
          setStatus("ready");
          setError(null);
          return;
        }
      }

      setStatus("error");
      setError(problemMessage(err, "Could not load your time."));
    }
  }, []);

  useEffect(() => {
    // No "already ran" ref guard, for the reason spelled out in useSession.ts: React 18
    // StrictMode mounts, tears down and remounts once in dev, and a ref that survived
    // that pair would wedge this on "loading" forever.
    const controller = new AbortController();

    /**
     * KNOWN OFFLINE — never even attempt the request. Mirrors `useSession.ts`'s
     * identical guard and `TimeClock.tsx`'s job-codes effect: a fetch that cannot
     * succeed has nothing to prove and only adds a browser-logged network error to a
     * screen that has to stay clean while offline (`offline-punch.spec.ts`'s own AC9
     * gate — a reload while offline, immediately before this effect would otherwise
     * have fired, is exactly the case that caught this same gap here). Falls back to
     * the identical "last known good" answer `load`'s own catch branch already gives
     * an unreachable network, so the visible result is the same either way — only the
     * doomed network attempt is gone.
     * @ui-check: this must stay reachable with no live API — the offline-reload path
     * (AC8) depends on it never attempting a request while `navigator.onLine` is false.
     */
    function resolveKnownOffline(): void {
      const cached = readMyTimeSnapshot();
      if (cached) {
        setData(cached);
        setStatus("ready");
        setError(null);
        return;
      }
      // Matches `problemMessage`'s own text for a real `ApiError` with `status === 0` —
      // the offline skip must read exactly like the live attempt it replaced would
      // have, not invent a second wording for the identical fact.
      setStatus("error");
      setError("No connection — this didn't reach the farm office.");
    }

    if (navigator.onLine) {
      void load(controller.signal);
    } else {
      resolveKnownOffline();
    }

    // The device just regained the signal the check above refused to spend on a
    // doomed request — ask for real now. Same 'online' retrigger `drainTriggers.ts`
    // and `useSession.ts` already use for the identical reason.
    const resume = (): void => void load();
    window.addEventListener("online", resume);

    return () => {
      controller.abort();
      window.removeEventListener("online", resume);
    };
  }, [load]);

  const reload = useCallback(() => load(), [load]);

  return { data, status, error, reload };
}
